import {
  convertDateToYYYYMMDDFormat,
  convertEventSelectToTarget,
  CustomDateRangePicker,
  DataTable,
  FormFeedBack,
  getFirstDayOfMonth,
  getToday,
  PageWrapper,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import { HandleProfessionalReport } from 'domain/usecases';
import { useFormik } from 'formik';
import { useState } from 'react';
import {
  Col,
  Row,
  Card,
  Form,
  Label,
  Spinner,
  CardBody,
  CardHeader,
  Input,
} from 'reactstrap';
import * as Yup from 'yup';

import noDataBg from '../../../assets/images/no-task.png';
import { ReportsGeneralFilters } from '../../Reports/components/reports-general-filters';
import { makeColumnsProfessionalReport } from '../common/columns/columns-professional-report';
import { useProfessionalReport } from '../hooks/useProfessionalReport';

interface IProps {
  handleProfessionalReport: HandleProfessionalReport;
}

const ProfessionalReport = ({ handleProfessionalReport }: IProps) => {
  const [buttonSelectedSubmit, setButtonSelectedSubmit] =
    useState<string>(null);

  // Hooks
  const {
    handlePerformanceReport,
    professionalReportResponse,
    handlePerformanceReportJson,
    statusGet,
    statusHandle,
  } = useProfessionalReport({
    handleProfessionalReport,
  });

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      unit: null,
      entity: null,
      groups: [],
      course: null,
      position: null,
      ch_max: null,
      starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
      ends: convertDateToYYYYMMDDFormat(getToday()),
      semester: {
        value: null,
      },
    },
    validationSchema: Yup.object({
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      ch_max: Yup.string().required('Campo Obrigatório.'),
      cargo: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      position: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string().nullable(),
          value: Yup.string().nullable(),
        })
        .nullable(),
      starts: Yup.date().required('Campo obrigatório.').nullable(),
      ends: Yup.date().required('Campo obrigatório.').nullable(),
    }),

    onSubmit: (values) => {
      const groupsValues = values.groups.map((group) => group.value);

      {
        buttonSelectedSubmit === 'search-button' &&
          handlePerformanceReportJson({
            type: '2',
            ends: values?.ends,
            groups: groupsValues,
            starts: values.starts,
            chMax: values.ch_max,
            entity: values.entity?.value,
            course: values.course?.value,
            role: values?.position?.value,
            semester: values.semester?.value,
            curricularUnit: values?.unit?.value,
          });
      }
      {
        buttonSelectedSubmit === 'export-excel' &&
          handlePerformanceReport({
            type: '1',
            ends: values?.ends,
            groups: groupsValues,
            starts: values.starts,
            chMax: values.ch_max,
            entity: values.entity?.value,
            course: values.course?.value,
            role: values?.position?.value,
            semester: values.semester?.value,
            curricularUnit: values?.unit?.value,
          });
      }
    },
  });

  return (
    <PageWrapper title="Desempenho Profissional">
      <Row>
        <Col lg={12} md={12} xs={12}>
          <CardHeader className="mb-4 border-0">
            <h4>Profissionais - Desempenho</h4>
          </CardHeader>
          <Card>
            <ReportsGeneralFilters
              validation={validation}
              buttonsChildren={
                <div className="d-flex gap-2 justify-content-end align-content-end">
                  <button
                    id="search"
                    type="submit"
                    onClick={() => {
                      setButtonSelectedSubmit('search-button');
                    }}
                    className="btn btn-primary waves-effect waves-light"
                    disabled={statusGet === STATUSPAGE.LOADING && true}
                  >
                    {statusGet === STATUSPAGE.LOADING ? (
                      <Spinner size="sm">{['Carregando...']}</Spinner>
                    ) : (
                      <>
                        Visualizar
                        <i className="ri-computer-line mx-2"></i>
                      </>
                    )}
                  </button>
                  <button
                    id="export-excel"
                    type="submit"
                    onClick={() => {
                      setButtonSelectedSubmit('export-excel');
                    }}
                    className="btn btn-light waves-effect waves-light"
                    disabled={statusHandle === STATUSPAGE.LOADING && true}
                  >
                    {buttonSelectedSubmit === 'export-excel' &&
                    statusHandle === STATUSPAGE.LOADING ? (
                      <Spinner size="sm">{['Carregando...']}</Spinner>
                    ) : (
                      <>
                        Exportar para Excel
                        <i className="ri-file-excel-2-line mx-2"></i>
                      </>
                    )}
                  </button>
                </div>
              }
              inputsChildren={
                <>
                  <Col lg={4} className="mb-3">
                    <Label htmlFor="final-date" className="form-label">
                      Cargo:
                    </Label>
                    <Select
                      isClearable
                      id="position"
                      name="position"
                      placeholder="Cargo"
                      value={validation.values.position}
                      onChange={(e) => {
                        validation.handleChange(
                          convertEventSelectToTarget('position', e),
                        );
                      }}
                      onBlur={validation.handleBlur}
                      options={[
                        {
                          label: 'Assistente',
                          value: '6',
                        },
                        {
                          label: 'Coordenador',
                          value: '5',
                        },
                        {
                          label: 'Preceptor',
                          value: '8',
                        },
                        {
                          label: 'Professor',
                          value: '7',
                        },
                      ]}
                      invalid={
                        !!(validation?.touched && validation?.errors?.position)
                      }
                    />
                    <FormFeedBack
                      error={validation?.errors?.position?.toString()}
                      isInvalid={
                        !!(validation?.touched && validation?.errors?.position)
                      }
                    />
                  </Col>

                  <Col sm={12} md={6} lg={4} className="mb-3">
                    <Row>
                      <Label htmlFor="initial-date" className="form-label">
                        Período
                      </Label>
                      <CustomDateRangePicker
                        initialStartDate={getFirstDayOfMonth()}
                        initialEndDate={getToday()}
                        placement="auto"
                        onDateChange={(start, ends) => {
                          validation.setFieldValue(
                            'starts',
                            convertDateToYYYYMMDDFormat(start),
                          );
                          validation.setFieldValue(
                            'ends',
                            convertDateToYYYYMMDDFormat(ends),
                          );
                        }}
                      />
                    </Row>
                  </Col>
                  <Col md={2}>
                    <Label htmlFor="final-date" className="form-label">
                      Carga Horária Máxima:
                    </Label>
                    <Input
                      type="number"
                      id="ch_max"
                      name="ch_max"
                      placeholder="Carga Horária Máxima:"
                      className="form-control"
                      value={validation.values.ch_max}
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      invalid={
                        !!(validation.touched && validation.errors.ch_max)
                      }
                    />
                    <FormFeedBack
                      error={validation?.errors?.ch_max?.toString()}
                      isInvalid={
                        !!(validation?.touched && validation?.errors?.ch_max)
                      }
                    />
                  </Col>
                </>
              }
            />
            <Form
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                validation.handleSubmit();
                return false;
              }}
            >
              {professionalReportResponse && (
                <CardBody className="p-4">
                  <Row>
                    <DataTable
                      noDataBg={noDataBg}
                      actions={[]}
                      loading={false}
                      canSearch={false}
                      data={professionalReportResponse || []}
                      columns={makeColumnsProfessionalReport()}
                    />
                  </Row>
                </CardBody>
              )}
            </Form>
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default ProfessionalReport;
